import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import Logo from '@/components/ui/logo';
import { useAppConfig } from '@/lib/hooks/useAppConfig';
import { getCompanyEmails } from '@/lib/config';

// Module-level flag so the slide-in only plays once per page load. Subsequent
// mounts (from route changes between public pages) render the buttons in
// their final state without re-animating.
let headerHasAnimated = false;

export default function PublicHeader() {
  const { waitingListMode } = useAppConfig();
  const gradientRef = useRef<HTMLDivElement>(null);
  const [shouldAnimate] = useState(() => !headerHasAnimated);
  const [menuOpen, setMenuOpen] = useState(false);
  useEffect(() => { headerHasAnimated = true; }, []);

  // Close the mobile menu on Escape and lock body scroll while it's open.
  useEffect(() => {
    if (!menuOpen) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setMenuOpen(false); };
    document.body.style.overflow = 'hidden';
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = '';
      window.removeEventListener('keydown', onKey);
    };
  }, [menuOpen]);

  useEffect(() => {
    let raf = 0;
    const update = () => {
      raf = 0;
      const el = gradientRef.current;
      if (!el) return;
      const raw = Math.min(1, Math.max(0, window.scrollY / 400));
      const eased = raw * raw * (3 - 2 * raw);
      el.style.opacity = String(eased * 0.7);
    };
    const onScroll = () => {
      if (raf) return;
      raf = requestAnimationFrame(update);
    };
    update();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      if (raf) cancelAnimationFrame(raf);
      window.removeEventListener('scroll', onScroll);
    };
  }, []);

  return (
    <header className="bg-transparent h-32 pt-5 pb-12 overflow-visible mb-[-24px] sticky top-0 z-50">
      <div
        ref={gradientRef}
        className="absolute inset-x-0 top-0 h-24 pointer-events-none"
        style={{
          opacity: 0,
          background: 'linear-gradient(to bottom, var(--header-fade) 0%, color-mix(in srgb, var(--header-fade) 98%, transparent) 25%, color-mix(in srgb, var(--header-fade) 88%, transparent) 50%, color-mix(in srgb, var(--header-fade) 55%, transparent) 75%, color-mix(in srgb, var(--header-fade) 0%, transparent) 100%)',
        }}
      />
      <div className="relative max-w-7xl mx-auto px-4 sm:px-8 lg:px-20 flex justify-between items-center overflow-visible">
        <div className="flex items-center space-x-4">
          {/* Hamburger — mobile only. Tablet+ shows the Pricing link inline. */}
          {!waitingListMode && (
            <Button
              type="button"
              variant="secondary"
              size="icon"
              aria-label={menuOpen ? 'Close menu' : 'Open menu'}
              aria-expanded={menuOpen}
              onClick={() => setMenuOpen((v) => !v)}
              className="md:hidden mr-4 relative z-50 border-0"
            >
              {/* Two icons stacked vertically inside a clipped square. Both
                  the wrapper and the icons use `size-7` (1.75rem) so they
                  scale with the page font and stay perfectly centred inside
                  the icon button. The `size-` prefix on the SVG class also
                  bypasses Button's `[&_svg:not([class*='size-'])]:size-4`
                  override that was shrinking them. Opening: hamburger slides
                  DOWN, then X slides UP after a short delay; closing reverses. */}
              <span className="relative inline-block size-7 overflow-hidden">
                <svg
                  viewBox="0 0 24 24"
                  fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"
                  className="absolute inset-0 size-7"
                  style={{
                    transform: menuOpen ? 'translateY(100%)' : 'translateY(0)',
                    transition: `transform 0.2s cubic-bezier(0.65, 0, 0.35, 1) ${menuOpen ? '0s' : '0.18s'}`,
                  }}
                >
                  <line x1="4" y1="7" x2="20" y2="7" />
                  <line x1="4" y1="13" x2="20" y2="13" />
                  <line x1="4" y1="19" x2="20" y2="19" />
                </svg>
                <svg
                  viewBox="0 0 24 24"
                  fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"
                  className="absolute inset-0 size-7"
                  style={{
                    transform: menuOpen ? 'translateY(0)' : 'translateY(-100%)',
                    transition: `transform 0.2s cubic-bezier(0.65, 0, 0.35, 1) ${menuOpen ? '0.18s' : '0s'}`,
                  }}
                >
                  <line x1="6" y1="6" x2="18" y2="18" />
                  <line x1="6" y1="18" x2="18" y2="6" />
                </svg>
              </span>
            </Button>
          )}
          <Link to="/" className="flex items-center">
            <Logo />
          </Link>
          {!waitingListMode && (
            <div className="hidden md:flex items-center space-x-0 ml-4">
              <Button asChild variant="ghost">
                <Link to="/pricing">
                  Pricing
                </Link>
              </Button>
            </div>
          )}
        </div>
        {!waitingListMode && (
          <div className="flex items-center space-x-4 overflow-visible py-3 -my-3 pr-3 -mr-3">
            <div
              style={shouldAnimate ? {
                opacity: 0,
                transform: 'translateY(110%)',
                animation: 'slideUpHeader 0.8s cubic-bezier(0.16, 1, 0.3, 1) 0.3s forwards',
              } : undefined}
            >
              <Button asChild variant="secondary" className="text-sm">
                <Link to="/sign-in">Log in</Link>
              </Button>
            </div>
            <div
              style={shouldAnimate ? {
                opacity: 0,
                transform: 'translateY(110%)',
                animation: 'slideUpHeader 0.8s cubic-bezier(0.16, 1, 0.3, 1) 0.4s forwards',
              } : undefined}
            >
              <Button asChild variant="primary">
                <Link to="/sign-up">Sign up for free</Link>
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* Mobile full-screen menu. Tablet+ uses the inline header instead.
          z-40 < header's z-50 so the morphing hamburger stays clickable
          on top of the menu to close it. */}
      {!waitingListMode && menuOpen && (
        <div className="md:hidden fixed inset-0 z-40 bg-surface-marketing flex flex-col">
          {/* Spacer so menu items don't sit under the header / hamburger. */}
          <div className="h-32 shrink-0" />
          <nav className="flex-1 flex flex-col px-6 gap-1">
            {[
              { to: '/pricing', label: 'Pricing' },
              { to: '/sign-up', label: 'Sign up' },
              { to: '/sign-in', label: 'Log In' },
              { href: `mailto:${getCompanyEmails().support}`, label: 'Contact Support' },
              { to: '/privacy', label: 'Privacy Policy' },
              { to: '/terms', label: 'Terms of Service' },
            ].map((item, i) => {
              const className = 'block py-4 text-2xl font-medium text-content-heading hover:bg-accent px-4 rounded-md';
              const animationStyle = {
                display: 'block',
                transform: 'translateY(110%)',
                animation: `slideUpClipped 0.6s cubic-bezier(0.16, 1, 0.3, 1) ${0.08 + i * 0.05}s forwards`,
              } as const;
              const close = () => setMenuOpen(false);
              return (
                <span
                  key={item.label}
                  style={{
                    display: 'block',
                    overflow: 'hidden',
                    // Padding + matching negative margin keeps the clip tight
                    // around the slide but lets the hover background bleed
                    // beyond it at rest.
                    padding: '0.5rem 0',
                    margin: '-0.5rem 0',
                  }}
                >
                  {'to' in item && item.to ? (
                    <Link to={item.to} onClick={close} className={className} style={animationStyle}>
                      {item.label}
                    </Link>
                  ) : (
                    <a href={item.href} onClick={close} className={className} style={animationStyle}>
                      {item.label}
                    </a>
                  )}
                </span>
              );
            })}
          </nav>
        </div>
      )}
    </header>
  );
}

